/**
 * US city suggestions for address forms — an assistant, never a gatekeeper: the
 * domain keeps accepting any non-empty city, because USPS recognises place names no
 * dataset carries.
 *
 * The dataset is the Census Gazetteer (see scripts/build-us-cities.mjs), committed as
 * a compact per-state JSON and pulled in through a dynamic import, so it becomes its
 * own lazy chunk: nothing here weighs on the initial bundle, and the file only
 * travels when a city field is actually used.
 *
 * `searchCities` is the module's contract with the UI. If Stage 2 ever needs places
 * server-side (lat/lon for loads), an endpoint-backed implementation drops in behind
 * the same signature.
 */

export interface CityMatch {
  city: string
  state: string
}

export interface CitySearchResult {
  /** The first page of matches, capped by the caller's limit. */
  matches: CityMatch[]
  /** How many matched in total — what the screen-reader announcement reports. */
  total: number
}

/**
 * `preferredState` is the state the form already holds: its places go first, so a
 * dispatcher who typed OH before the city finds Columbus, OH on top (TRKC-103).
 */
export type CitySearch = (
  term: string,
  limit: number,
  preferredState?: string,
) => Promise<CitySearchResult>

/** A city typed with its state after it: "Columbus, OH", "columbus oh". */
export interface CityTerm {
  city: string
  /** The state as typed so far, upper-cased: "OH", or "O" while it is being typed. */
  state: string
}

// "City, ST" — the comma may stand alone while the state is still to come — or
// "City ST", where the state needs at least one letter to be told from the name.
const TRAILING_STATE = /^(.*?\S)\s*(?:,\s*([a-z]{0,2})|\s+([a-z]{1,2}))$/i

/**
 * Reads a state typed after the city — the way DAT and every rate confirmation write a
 * place. Only a reading: the search takes it only when the whole term names no place,
 * so "Saint Al" still finds Saint Albans and never becomes Saint, Alabama.
 */
export function splitCityTerm(term: string): CityTerm | null {
  const found = TRAILING_STATE.exec(term.trim())
  if (found === null) {
    return null
  }
  return { city: found[1]!, state: (found[2] ?? found[3] ?? '').toUpperCase() }
}

/**
 * Lowercases and strips diacritics one character at a time, so the folded string is
 * the same length as the original and match positions carry straight back to it:
 * "Cañon City" and "canon" meet without anyone typing the ñ.
 */
export function foldCityText(text: string): string {
  let folded = ''
  for (const character of text) {
    folded += (character.normalize('NFD')[0] ?? character).toLowerCase()
  }
  return folded
}

/**
 * Where the term hits the city name, for highlighting: at the start, or at a word
 * boundary (space or hyphen — "spr" finds West Springfield, "salem" finds
 * Winston-Salem). Null when it does not match.
 */
export function matchCity(city: string, term: string): { index: number; length: number } | null {
  const needle = foldCityText(term.trim())
  if (needle === '') {
    return null
  }

  const folded = foldCityText(city)
  if (folded.startsWith(needle)) {
    return { index: 0, length: needle.length }
  }

  for (const boundary of [' ', '-']) {
    const at = folded.indexOf(boundary + needle)
    if (at >= 0) {
      return { index: at + 1, length: needle.length }
    }
  }

  return null
}

export interface IndexedCity extends CityMatch {
  folded: string
}

/** Expands the per-state compact form into one globally sorted, pre-folded index. */
export function buildCityIndex(byState: Record<string, string>): IndexedCity[] {
  const entries: IndexedCity[] = []

  for (const [state, joined] of Object.entries(byState)) {
    for (const city of joined.split('|')) {
      entries.push({ city, state, folded: foldCityText(city) })
    }
  }

  // One global order, so results read alphabetically regardless of source state.
  entries.sort((a, b) =>
    a.folded === b.folded ? a.state.localeCompare(b.state) : a.folded.localeCompare(b.folded),
  )
  return entries
}

/**
 * The ranking itself, separated from the lazy dataset so it can be unit-tested with a
 * fixture: name-starts-with matches first, word matches after, each group in index
 * order; `total` counts everything, `matches` carries only the first page.
 *
 * Sixteen places are called Columbus, and the page holds ten: in index order Ohio's
 * came 13th, and 697 places could not be picked even with their whole name typed
 * (TRKC-103). So a term that names no place is read again as "City, ST", and the
 * preferred state's places lead the page.
 */
export function searchCityIndex(
  index: IndexedCity[],
  term: string,
  limit: number,
  preferredState?: string,
): CitySearchResult {
  const needle = foldCityText(term.trim())
  if (needle === '') {
    return { matches: [], total: 0 }
  }

  let ranked = rankByName(index, needle)
  if (ranked.length === 0) {
    const split = splitCityTerm(term)
    const cityNeedle = split === null ? '' : foldCityText(split.city)
    if (split !== null && cityNeedle !== '') {
      ranked = rankByName(index, cityNeedle).filter((entry) => entry.state.startsWith(split.state))
    }
  }

  // A state box holding half a code matches no place's state, and so moves nothing.
  const preferred = preferredState?.trim().toUpperCase()
  if (preferred) {
    ranked = [
      ...ranked.filter((entry) => entry.state === preferred),
      ...ranked.filter((entry) => entry.state !== preferred),
    ]
  }

  return {
    matches: ranked.slice(0, limit).map(({ city, state }) => ({ city, state })),
    total: ranked.length,
  }
}

function rankByName(index: IndexedCity[], needle: string): IndexedCity[] {
  const atStart: IndexedCity[] = []
  const atWord: IndexedCity[] = []

  for (const entry of index) {
    if (entry.folded.startsWith(needle)) {
      atStart.push(entry)
    } else if (entry.folded.includes(' ' + needle) || entry.folded.includes('-' + needle)) {
      atWord.push(entry)
    }
  }

  return [...atStart, ...atWord]
}

let indexPromise: Promise<IndexedCity[]> | null = null

function loadIndex(): Promise<IndexedCity[]> {
  indexPromise ??= import('./us-cities.json')
    .then((module) => buildCityIndex(module.default as Record<string, string>))
    .catch((error: unknown) => {
      // A failed chunk load must not poison every later search: un-cache it here,
      // at the source, so whichever call comes next retries the import.
      indexPromise = null
      throw error
    })
  return indexPromise
}

/** Starts fetching the dataset chunk so the first real search finds it warm. */
export function preloadCities(): void {
  // A failed preload is not an event: loadIndex un-caches itself on rejection, and
  // the search that actually needs the data will retry and surface its own silence.
  void loadIndex().catch(() => {})
}

export const searchCities: CitySearch = async (term, limit, preferredState) => {
  // Guarded before the index loads: short terms must not pull the dataset chunk.
  if (foldCityText(term.trim()).length < 2) {
    return { matches: [], total: 0 }
  }

  return searchCityIndex(await loadIndex(), term, limit, preferredState)
}
